import { useEffect, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { MagnifyingGlassIcon, PencilIcon, PlusIcon, TrashIcon } from '@heroicons/react/24/outline';
import { programAPI } from '../../../services/programApi';
import { Program } from '../../../types/program';
import Breadcrumb from '../../../components/layout/Breadcrumb';
import Button from '../../../components/ui/Button';
import Card from '../../../components/ui/Card';
import EmptyState from '../../../components/ui/EmptyState';
import ErrorState from '../../../components/ui/ErrorState';
import Input from '../../../components/ui/Input';
import LoadingSkeleton from '../../../components/ui/LoadingSkeleton';
import ToastContainer from '../../../components/ui/ToastContainer';
import { useToast } from '../../../hooks/useToast';

export default function ProgramList() {
  const navigate = useNavigate();
  const [programs, setPrograms] = useState<Program[]>([]);
  const [filtered, setFiltered] = useState<Program[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<Error | null>(null);
  const [searchQuery, setSearchQuery] = useState('');
  const [deletingId, setDeletingId] = useState<string | null>(null);
  const { toasts, success, error: showError, removeToast } = useToast();

  const fetchPrograms = async () => {
    try {
      setLoading(true);
      setError(null);
      const data = await programAPI.fetchPrograms({ includeDrafts: true });
      setPrograms(data);
      setFiltered(data);
    } catch (err) {
      setError(err instanceof Error ? err : new Error('Failed to load programs'));
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    fetchPrograms();
  }, []);

  useEffect(() => {
    const query = searchQuery.trim().toLowerCase();
    if (!query) {
      setFiltered(programs);
      return;
    }
    setFiltered(
      programs.filter(
        (program) =>
          program.title.toLowerCase().includes(query) ||
          program.slug.toLowerCase().includes(query) ||
          (program.introduction || '').toLowerCase().includes(query)
      )
    );
  }, [searchQuery, programs]);

  const handleDelete = async (program: Program) => {
    if (!confirm(`Delete program "${program.title}"? Its hubs stay, and become unassigned.`)) return;
    try {
      setDeletingId(program.id);
      await programAPI.deleteProgram(program.id);
      setPrograms((prev) => prev.filter((item) => item.id !== program.id));
      success('Program deleted');
    } catch (err) {
      showError(err instanceof Error ? err.message : 'Failed to delete program');
    } finally {
      setDeletingId(null);
    }
  };

  if (loading) {
    return (
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-8">
        <LoadingSkeleton lines={8} />
      </div>
    );
  }

  if (error) {
    return (
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-8">
        <ErrorState error={error} title="Failed to load programs and references" onRetry={fetchPrograms} />
      </div>
    );
  }

  return (
    <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-8">
      <ToastContainer toasts={toasts} onClose={removeToast} />
      <Breadcrumb items={[{ label: 'Home', to: '/' }, { label: 'Programs and references' }]} />

      <div className="mb-6 flex items-center justify-between gap-4">
        <div>
          <h1 className="text-3xl font-bold text-gray-900">Programs and references</h1>
          <p className="mt-1 text-sm text-gray-600">
            A program is a clinical topic. A reference is material you look up. Assign toolkits, and add headings and documents.
          </p>
        </div>
        <Button onClick={() => navigate('/admin/programs/new')} className="flex items-center gap-2">
          <PlusIcon className="h-5 w-5" />
          Create program
        </Button>
      </div>

      <div className="mb-6">
        <div className="relative">
          <MagnifyingGlassIcon className="absolute left-3 top-1/2 h-5 w-5 -translate-y-1/2 text-gray-400" />
          <Input
            type="text"
            placeholder="Search programs by title, introduction, or slug..."
            value={searchQuery}
            onChange={(e) => setSearchQuery(e.target.value)}
            className="pl-10"
          />
        </div>
      </div>

      {filtered.length === 0 ? (
        <EmptyState
          title={searchQuery ? 'No programs found' : 'No programs yet'}
          description={
            searchQuery ? 'Try adjusting your search query' : 'Get started by creating your first program'
          }
          actionLabel={!searchQuery ? 'Create Program' : undefined}
          onAction={!searchQuery ? () => navigate('/admin/programs/new') : undefined}
        />
      ) : (
        <div className="space-y-4">
          {filtered.map((program) => (
            <Card key={program.id} className="p-6">
              <div className="flex items-start justify-between gap-4">
                <div className="min-w-0 flex-1">
                  <div className="mb-1 flex items-center gap-2">
                    <h3 className="text-lg font-semibold text-gray-900">{program.title}</h3>
                    {program.status === 'DRAFT' && (
                      <span className="rounded-full bg-amber-100 px-2 py-0.5 text-xs font-medium text-amber-800">
                        Draft
                      </span>
                    )}
                  </div>
                  {program.introduction && (
                    <p className="mb-3 line-clamp-2 text-sm text-gray-600">{program.introduction}</p>
                  )}
                  <div className="flex flex-wrap items-center gap-x-3 gap-y-1 text-sm text-gray-500">
                    <span>Slug: {program.slug}</span>
                    <span>{program.hubs.length} {program.hubs.length === 1 ? 'hub' : 'hubs'}</span>
                    <span>{program.sections.length} {program.sections.length === 1 ? 'section' : 'sections'}</span>
                    <span>{program.highlights.length} highlighted</span>
                  </div>
                </div>
                <div className="flex gap-2">
                  <Button
                    variant="ghost"
                    className="!px-3 !py-2"
                    onClick={() => navigate(`/admin/programs/${program.id}/edit`)}
                    aria-label={`Edit ${program.title}`}
                  >
                    <PencilIcon className="h-5 w-5" />
                  </Button>
                  <Button
                    variant="ghost"
                    className="!px-3 !py-2"
                    onClick={() => handleDelete(program)}
                    disabled={deletingId === program.id}
                    aria-label={`Delete ${program.title}`}
                  >
                    <TrashIcon className="h-5 w-5 text-error" />
                  </Button>
                </div>
              </div>
            </Card>
          ))}
        </div>
      )}
    </div>
  );
}
